import * as React from "react";
import {CommonAssociationFeature, AssociationScope, Attribute} from "../../../contract-features-3/domain";
import {AssociatedFeature} from "../../domain/AssociatedFeature";
import {AssociationJson} from "../../domain/AssociationJson";
import {Popover, OverlayTrigger} from 'react-bootstrap';
import classNames from 'classnames';
import {ToggleSelectedAssociationTarget} from "../../../../actions/actions";
import {FeatureTypeAttributes} from "../../state/AssociationsState";

interface FeatureListProps {
    features: CommonAssociationFeature[];
    selectedFeatures: CommonAssociationFeature[];
    onFeatureClick: ToggleSelectedAssociationTarget;
    selectedAssociation: AssociatedFeature;
    associationsFlat: AssociationJson[];
    enforceNoNvdbMothers?: boolean;
    featureType: FeatureTypeAttributes;
    tabType: AssociationScope;
    disableDescriptiveName: boolean;
}

interface FeatureListState {

}

export default class FeatureList extends React.Component<FeatureListProps, FeatureListState> {
    constructor(props: FeatureListProps) {
        super(props);
    }

    getParent(feature: CommonAssociationFeature){
        const flat = this.props.associationsFlat;
        let association = flat.find(a => a.childFeatureId === feature.id);
        return association;
    }

    isChildToTarget(feature: CommonAssociationFeature) {
        const flat = this.props.associationsFlat;
        return flat.some(a => a.parentFeatureId === feature.id && a.childFeatureId === this.props.selectedAssociation.id) ;
    }

    isParentToTarget(feature: CommonAssociationFeature) {
        // child ids consist of nvdb id + version
        return this.props.selectedAssociation.childFeatures.some(childFeature => childFeature.id.split(":")[0] == feature.id);
    }

    isDeletedChild(feature: CommonAssociationFeature): boolean {
        const flat = this.props.associationsFlat;
        return flat.some(a => a.operation === "DELETE" && a.childFeatureId === feature.id)
    }

    isClosing(feature: CommonAssociationFeature): boolean {
        return (feature.operation === "CLOSE");
    }
    
    render() {
        const {selectedFeatures, featureType, features, disableDescriptiveName} = this.props;

        let featureList: React.ReactElement[] = [];

        // Filter out CLOSEING
        const featuresFiltered = features.filter(f => this.isClosing(f) === false);

        if (featuresFiltered.length > 0 && features[0].typeId == featureType.typeId) {     // gotcha! briefly, features and featureType will be out of sync
            
            featureList = featuresFiltered.map(feature => {

                let selected = selectedFeatures.filter(f => f.id === feature.id).length > 0;
                let isChildToTarget = this.isChildToTarget(feature);
                let isParentToTarget = this.isParentToTarget(feature);
                let isInTargetAssociation = isChildToTarget || isParentToTarget;
                let existingContractAssociation = this.getParent(feature);
                let hasNvdbMother = this.props.enforceNoNvdbMothers && feature.associations.foreldre && feature.associations.foreldre.length > 0;
                const isDeleted = this.isDeletedChild(feature);

                const classes = classNames({
                    associationCandidate: true,
                    selected: selected,
                    isInTargetAssociation: isInTargetAssociation && !isDeleted,
                    isDeleted: isDeleted
                });


                let attributeSorter = function(a: Attribute, b: Attribute){return a.typeId - b.typeId};

                let attributePopover = (
                    <Popover id={'attributes' + feature.id} title={"Egenskaper"}>
                        <dl>
                            {feature.attributes
                                .sort(attributeSorter)
                                .map(att => {
                                    let term = att.name? att.name : featureType.attributes[att.typeId].name;   // attributes are different for nvdb or project features
                                    if (term.startsWith("Geometri,")) return null;      // geometri kan være svært kompleks så vi skjuler denne
                                    return (<span key={att.typeId}><dt >{term}</dt><dd>{att.value}</dd></span>);
                                }).filter(a => a != null)}
                        </dl>
                    </Popover>
                );


                let popover;
                if (existingContractAssociation) {
                    if (isDeleted) {
                        popover = (
                            <Popover id={'foreignlink' + existingContractAssociation.childFeatureId}
                                     title={"Fjernet kobling"}>
                                Dette objektet var koblet til {existingContractAssociation.parentFeature}, <br/>
                                kobling vil bli slettet ved innsending til NVDB.
                            </Popover>
                        );
                    } else {
                        popover = (
                            <Popover id={'foreignlink' + existingContractAssociation.childFeatureId}
                                     title={"Eksisterende kobling"}>
                                Dette objektet er koblet til {existingContractAssociation.parentFeature}
                            </Popover>
                        );
                    }
                }  else if (hasNvdbMother) {
                    popover = (
                        <Popover id={'foreignlink' + feature.associations.foreldre[0].vegobjekter[0].id} title={"Eksisterende kobling"}>
                            Dette objektet har mor i NVDB
                        </Popover>
                    );
                }

                let nvdbStatusPopover: React.ReactNode;
                let nvdbStatusIcon: string;
                if (feature.isExporting) {
                    nvdbStatusPopover = (<Popover id={"NvdbStatus" + feature.id}>
                        Dette objektet er under eksport til NVDB
                    </Popover>);
                    nvdbStatusIcon = "glyphicon-save";
                } else if (!feature.isDirty) {
                    nvdbStatusPopover = (<Popover id={"NvdbStatus" + feature.id}>
                        Dette objektet er synkronisert med NVDB
                    </Popover>);
                    nvdbStatusIcon = "glyphicon-saved";
                }

                const name = disableDescriptiveName ? feature.name : feature.descriptiveName;

                return (
                    <div key={feature.id} className={classes} onClick={() => this.props.onFeatureClick(feature)} title={feature.name}>
                        {name}

                        <span className="icons">
                            <OverlayTrigger trigger={['hover']} placement="right" overlay={attributePopover}>
                                <span className="glyphicon glyphicon-info-sign float-right" ></span>
                            </OverlayTrigger>

                            {((existingContractAssociation || hasNvdbMother) && !isDeleted) && (
                                <OverlayTrigger trigger={['hover']} placement="right" overlay={popover}>
                                    <span className="glyphicon glyphicon-link float-right" ></span>
                                </OverlayTrigger>
                            )}

                            {(isDeleted && (
                                <OverlayTrigger trigger={['hover']} placement="right" overlay={popover}>
                                    <span className="glyphicon glyphicon-scissors float-right" />
                                </OverlayTrigger>
                            ))}

                            {nvdbStatusPopover &&
                                <OverlayTrigger trigger={['hover']} placement="right" overlay={nvdbStatusPopover}>
                                    <span className={'glyphicon ' + nvdbStatusIcon + ' float-right'} />
                                </OverlayTrigger>
                            }
                        </span>

                    </div>
                )
            });
        }

        const heightCss = (this.props.tabType == "PROJECT_TO_PROJECT") ? "to-contract" : "to-nvdb";
        return (
            <div id="targetAssociatonsList" className={heightCss}>
                {featureList}
            </div>

        );
    }

}